import { InputField } from '@/components/form/InputField';
import type { SendToEmailFormValues } from '@/types/sendTicketsToEmail';
import { Button, Icons, Typography } from '@packages/webshop-design-system';
import { type FC, useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface PurchaserEmailRowProps {
  holderName: string;
  defaultEmail: string;
}

const PurchaserEmailRow: FC<PurchaserEmailRowProps> = ({
  holderName,
  defaultEmail,
}) => {
  const { control, setValue } = useFormContext<SendToEmailFormValues>();
  const [isEditing, setIsEditing] = useState(false);
  const [editedEmail, setEditedEmail] = useState('');

  const currentEmail =
    useWatch({
      control,
      name: 'purchaser.emailOverride.0',
    }) || defaultEmail;

  const handleEdit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Pre-fill with current email value
      setEditedEmail(currentEmail);
      setIsEditing(true);
    },
    [currentEmail]
  );

  const handleSubmit = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      // Update form's emailOverride[0] with edited value
      setValue('purchaser.emailOverride.0', editedEmail);
      setIsEditing(false);
    },
    [editedEmail, setValue]
  );

  const handleDiscard = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    // Just exit edit mode - form keeps its current value
    setEditedEmail('');
    setIsEditing(false);
  }, []);

  return (
    <div className="flex flex-row gap-3 rounded-lg border border-neutral-light p-3">
      <div className="flex flex-1 flex-col">
        <Typography variant="body1-bold">{holderName}</Typography>
        {!isEditing ? (
          <Typography variant="body2" className="text-neutral">
            {currentEmail || '-'}
          </Typography>
        ) : (
          <InputField
            name="purchaser-email-edit"
            label="email"
            hideLabel
            value={editedEmail}
            onChange={(e) => setEditedEmail(e.target.value)}
            endAdornment={
              editedEmail ? (
                <Button
                  variant="ghost"
                  onClick={() => setEditedEmail('')}
                  data-testid="clear-purchaser-email"
                  aria-label="Clear email"
                  className="text-dark"
                >
                  <Icons.xSmall />
                </Button>
              ) : null
            }
          />
        )}
      </div>
      {!isEditing ? (
        <Button
          variant="secondary"
          size="small"
          className="aspect-square rounded-full p-2"
          type="button"
          onClick={handleEdit}
          data-testid="edit-purchaser-email"
        >
          <Icons.edit width={16} height={16} />
        </Button>
      ) : (
        <div className="flex flex-col gap-2">
          <Button
            variant="secondary"
            size="small"
            type="button"
            isDisabled={!editedEmail}
            onClick={handleSubmit}
            data-testid="submit-purchaser-email"
            className="aspect-square rounded-full p-2 text-success-text"
          >
            <Icons.check width={16} height={16} />
          </Button>
          <Button
            variant="secondary"
            size="small"
            type="button"
            onClick={handleDiscard}
            data-testid="discard-purchaser-email"
            className="aspect-square rounded-full p-2 text-error-text"
          >
            <Icons.xSmall width={16} height={16} />
          </Button>
        </div>
      )}
    </div>
  );
};

export default PurchaserEmailRow;
